import {
  Database,
  Users,
  Building2,
  Package,
  FileText,
  Settings,
  MessageSquare,
  Globe
} from 'lucide-react'

// Import the detailed schema data
import { completeDatabaseSchema as schemaDetails } from './complete_database_schema'

export type ColumnInfo = {
  name: string
  type: string
  nullable: boolean
  default: string | null
  constraints: string[]
}

export type ForeignKey = {
  column: string
  references: string
}

export type IndexInfo = {
  name: string
  columns: string[]
  type?: string
}

export type TableSchema = {
  columns: ColumnInfo[]
  primaryKeys: string[]
  foreignKeys: ForeignKey[]
  indexes: IndexInfo[]
}

export type EnrichedTableInfo = {
  name: string
  records: string
  description: string
  schema: TableSchema
}

export type SchemaCategory = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: any
  color: string
  description: string
  tables: EnrichedTableInfo[]
  relationships: string[]
}

// Helper function to get schema details for a table
function getTableSchema(tableName: string): TableSchema {
  const details = (schemaDetails as Record<string, TableSchema>)[tableName]

  if (!details) {
    return {
      columns: [],
      primaryKeys: [],
      foreignKeys: [],
      indexes: []
    }
  }

  return details
}

// Enhanced schema with detailed column information
export const enrichedSchemaOverview: Record<string, SchemaCategory> = {
  // Authentication & Core System (12 tables)
  authentication: {
    icon: Database,
    color: 'bg-blue-500',
    description:
      'User authentication, tenant management, and core system infrastructure',
    tables: [
      {
        name: 'account_emailuser',
        records: '~10,353',
        description:
          'Primary user accounts with role assignments (mandant, supplier, partner links)',
        schema: getTableSchema('account_emailuser')
      },
      {
        name: 'account_account',
        records: '~17,771',
        description: 'Secondary account system with tenant isolation',
        schema: getTableSchema('account_account')
      },
      {
        name: 'account_emailuser_vertriebskanaele',
        records: '~1,796',
        description: 'User to sales channel assignments',
        schema: getTableSchema('account_emailuser_vertriebskanaele')
      },
      {
        name: 'account_emailusergroup',
        records: '~106,123',
        description: 'User permission group memberships',
        schema: getTableSchema('account_emailusergroup')
      },
      {
        name: 'account_emailuserotpemaildevice',
        records: '~130',
        description: '2FA email device configurations',
        schema: getTableSchema('account_emailuserotpemaildevice')
      },
      {
        name: 'account_hashedtoken',
        records: '~535',
        description: 'API authentication tokens',
        schema: getTableSchema('account_hashedtoken')
      },
      {
        name: 'auth_group',
        records: '~6',
        description: 'Django authentication groups',
        schema: getTableSchema('auth_group')
      },
      {
        name: 'auth_permission',
        records: '~1,229',
        description: 'System permissions catalog',
        schema: getTableSchema('auth_permission')
      },
      {
        name: 'auth_group_permissions',
        records: '~0',
        description: 'Group permission assignments',
        schema: getTableSchema('auth_group_permissions')
      },
      {
        name: 'authtoken_token',
        records: '~0',
        description: 'API tokens for external access',
        schema: getTableSchema('authtoken_token')
      },
      {
        name: 'permission_permissiongroup',
        records: '~314',
        description: 'Custom permission groups with product type scope',
        schema: getTableSchema('permission_permissiongroup')
      },
      {
        name: 'permission_platformpermission',
        records: '~244',
        description: 'Platform-specific permissions',
        schema: getTableSchema('permission_platformpermission')
      }
    ],
    relationships: [
      'account_emailuser → mandant_mandant (tenant isolation)',
      'account_emailuser → dienstleister_* (partner assignments)',
      'account_emailusergroup → permission_permissiongroup (permissions)',
      'All user tables → mandant_mandant (multi-tenant architecture)'
    ]
  },

  // Tenant Management (8 tables)
  tenants: {
    icon: Building2,
    color: 'bg-green-500',
    description:
      'Multi-tenant architecture with organization-specific configurations',
    tables: [
      {
        name: 'mandant_mandant',
        records: '~224',
        description:
          'Primary tenant/client organizations with full company details',
        schema: getTableSchema('mandant_mandant')
      },
      {
        name: 'mandant_mandant_erreichbarkeit',
        records: '~402',
        description: 'Tenant availability time windows',
        schema: getTableSchema('mandant_mandant_erreichbarkeit')
      },
      {
        name: 'mandant_mandantstaticmodel',
        records: '~15',
        description: 'Tenant branding and static configurations',
        schema: getTableSchema('mandant_mandantstaticmodel')
      },
      {
        name: 'mandant_region',
        records: '~15',
        description: 'Geographic regions for energy calculations',
        schema: getTableSchema('mandant_region')
      },
      {
        name: 'mandant_mandantumsetzungspartner',
        records: '~19',
        description: 'Implementation partner assignments per tenant',
        schema: getTableSchema('mandant_mandantumsetzungspartner')
      },
      {
        name: 'mandant_mandantumsetzungspartner_vertriebskanaele',
        records: '~0',
        description: 'Sales channels for implementation partners',
        schema: getTableSchema(
          'mandant_mandantumsetzungspartner_vertriebskanaele'
        )
      },
      {
        name: 'mandant_mandantvertriebsdienstleister',
        records: '~240',
        description: 'Sales service provider assignments',
        schema: getTableSchema('mandant_mandantvertriebsdienstleister')
      },
      {
        name: 'mandant_mandantvertriebsdienstleister_vertriebskanaele',
        records: '~295',
        description: 'Sales channel routing configurations',
        schema: getTableSchema(
          'mandant_mandantvertriebsdienstleister_vertriebskanaele'
        )
      }
    ],
    relationships: [
      'mandant_mandant → ALL major entities (tenant isolation)',
      'mandant_mandant → energiedach_edparameter (calculation configs)',
      'mandant_mandant → dienstleister_* (partner network)',
      'mandant_region → energiedach calculations (regional factors)'
    ]
  },

  // Customer Management (8 tables)
  customers: {
    icon: Users,
    color: 'bg-purple-500',
    description: 'Customer records, contacts, preferences, and geographic data',
    tables: [
      {
        name: 'kunde_kunde',
        records: '~434,154',
        description:
          'Primary customer records with address and company information',
        schema: getTableSchema('kunde_kunde')
      },
      {
        name: 'kunde_person',
        records: '~434,292',
        description:
          'Contact persons linked to customers with communication details',
        schema: getTableSchema('kunde_person')
      },
      {
        name: 'kunde_branche',
        records: '~31',
        description: 'Industry/sector classifications for customers',
        schema: getTableSchema('kunde_branche')
      },
      {
        name: 'kunde_erreichbarkeit',
        records: '~36',
        description: 'Availability time slot definitions',
        schema: getTableSchema('kunde_erreichbarkeit')
      },
      {
        name: 'kunde_postleitzahl',
        records: '~27,294',
        description: 'Postal code database with GPS coordinates',
        schema: getTableSchema('kunde_postleitzahl')
      },
      {
        name: 'kunde_kunde_erreichbarkeit',
        records: '~268,028',
        description: 'Customer availability preferences',
        schema: getTableSchema('kunde_kunde_erreichbarkeit')
      },
      {
        name: 'kunde_consent',
        records: '~14,883',
        description: 'GDPR consent management per person',
        schema: getTableSchema('kunde_consent')
      },
      {
        name: 'kunde_optincontactchannel',
        records: '~5,607',
        description: 'Communication channel preferences (email, SMS, phone)',
        schema: getTableSchema('kunde_optincontactchannel')
      }
    ],
    relationships: [
      'kunde_kunde → mandant_mandant (tenant separation)',
      'kunde_person → kunde_kunde (1:many contacts)',
      'kunde_person → account_account (customer portal access)',
      'kunde_kunde → projekt_projekt (via junction table)'
    ]
  },

  // Project Management (11 tables)
  projects: {
    icon: FileText,
    color: 'bg-red-500',
    description:
      'Core project lifecycle, status tracking, and metadata management',
    tables: [
      {
        name: 'projekt_projekt',
        records: '~479,414',
        description:
          'Central project records with status, assignments, and business logic',
        schema: getTableSchema('projekt_projekt')
      },
      {
        name: 'projekt_projekt_kunden',
        records: '~470,685',
        description: 'Many-to-many: projects to customers',
        schema: getTableSchema('projekt_projekt_kunden')
      },
      {
        name: 'projekt_projekt_tags',
        records: '~1,478,875',
        description: 'Public tagging system for projects',
        schema: getTableSchema('projekt_projekt_tags')
      },
      {
        name: 'projekt_intern_tags',
        records: '~10,828',
        description: 'Private tags visible only to specific organizations',
        schema: getTableSchema('projekt_intern_tags')
      },
      {
        name: 'projekt_projektnotiz',
        records: '~167,210',
        description: 'Project notes segregated by organization type',
        schema: getTableSchema('projekt_projektnotiz')
      },
      {
        name: 'projekt_projektwiedervorlage',
        records: '~1,583,185',
        description: 'Follow-up reminders and deadline tracking',
        schema: getTableSchema('projekt_projektwiedervorlage')
      },
      {
        name: 'projekt_sharedprojektnotizen',
        records: '~336,194',
        description: 'Shared notes between partner organizations',
        schema: getTableSchema('projekt_sharedprojektnotizen')
      },
      {
        name: 'projekt_projektkommunikationsaktion',
        records: '~0',
        description: 'Communication workflow triggers',
        schema: getTableSchema('projekt_projektkommunikationsaktion')
      },
      {
        name: 'projekt_berechnung',
        records: '~481,020',
        description: 'Project calculation results and caching',
        schema: getTableSchema('projekt_berechnung')
      },
      {
        name: 'projekt_absagegrundkategorie',
        records: '~4',
        description: 'Project rejection reason categories',
        schema: getTableSchema('projekt_absagegrundkategorie')
      },
      {
        name: 'projekt_absagegrund',
        records: '~64',
        description: 'Specific project rejection reasons',
        schema: getTableSchema('projekt_absagegrund')
      }
    ],
    relationships: [
      'projekt_projekt → kunde_kunde (via junction table)',
      'projekt_projekt → mandant_mandant (tenant ownership)',
      'projekt_projekt → dienstleister_* (partner assignments)',
      'projekt_projekt → produkt_produkt, paket_paket (product assignments)',
      'projekt_projekt → qualifikation_* (qualification data)',
      'projekt_projekt → abwicklung_*, angebot_*, bestellung_* (process flows)'
    ]
  },

  // Partner Network (20 tables)
  partners: {
    icon: Building2,
    color: 'bg-indigo-500',
    description:
      'Complex partner ecosystem with 4 partner types and regional assignments',
    tables: [
      // System Partners (Suppliers)
      {
        name: 'dienstleister_systempartner',
        records: '~740',
        description:
          'System partners providing products and installation services',
        schema: getTableSchema('dienstleister_systempartner')
      },
      {
        name: 'dienstleister_systempartner_fachpartner',
        records: '~913',
        description: 'System partner to specialist relationships',
        schema: getTableSchema('dienstleister_systempartner_fachpartner')
      },
      {
        name: 'dienstleister_systempartner_mandanten',
        records: '~108',
        description: 'System partner tenant assignments',
        schema: getTableSchema('dienstleister_systempartner_mandanten')
      },
      {
        name: 'dienstleister_systempartner_email_forward_to',
        records: '~5',
        description: 'Email forwarding for system partners',
        schema: getTableSchema('dienstleister_systempartner_email_forward_to')
      },
      {
        name: 'dienstleister_systempartnerprodukttyp',
        records: '~285',
        description: 'Product type assignments for system partners',
        schema: getTableSchema('dienstleister_systempartnerprodukttyp')
      },
      // Specialist Partners
      {
        name: 'dienstleister_fachpartner',
        records: '~1,183',
        description: 'Specialist partners for technical expertise',
        schema: getTableSchema('dienstleister_fachpartner')
      },
      {
        name: 'dienstleister_fachpartner_regionen',
        records: '~71,284',
        description: 'Regional coverage assignments (postal codes)',
        schema: getTableSchema('dienstleister_fachpartner_regionen')
      },
      {
        name: 'dienstleister_fachpartner_mandanten',
        records: '~32',
        description: 'Specialist partner tenant assignments',
        schema: getTableSchema('dienstleister_fachpartner_mandanten')
      },
      {
        name: 'dienstleister_fachpartner_email_forward_to',
        records: '~73',
        description: 'Email forwarding configurations',
        schema: getTableSchema('dienstleister_fachpartner_email_forward_to')
      },
      {
        name: 'dienstleister_fachpartner_wholesalers',
        records: '~0',
        description: 'Wholesaler relationship mapping',
        schema: getTableSchema('dienstleister_fachpartner_wholesalers')
      },
      {
        name: 'dienstleister_fachpartnerprodukttyp',
        records: '~1,714',
        description: 'Product expertise assignments',
        schema: getTableSchema('dienstleister_fachpartnerprodukttyp')
      },
      // Sales Partners
      {
        name: 'dienstleister_vertriebsdienstleister',
        records: '~60',
        description: 'Sales service providers and lead generators',
        schema: getTableSchema('dienstleister_vertriebsdienstleister')
      },
      {
        name: 'dienstleister_vdlprodukttyp',
        records: '~291',
        description: 'Sales partner product assignments',
        schema: getTableSchema('dienstleister_vdlprodukttyp')
      },
      // Implementation Partners
      {
        name: 'dienstleister_umsetzungspartner',
        records: '~5',
        description: 'Implementation and delivery specialists',
        schema: getTableSchema('dienstleister_umsetzungspartner')
      },
      {
        name: 'dienstleister_umsetzungspartnerprodukttyp',
        records: '~14',
        description: 'Implementation partner capabilities',
        schema: getTableSchema('dienstleister_umsetzungspartnerprodukttyp')
      },
      // Notification Systems
      {
        name: 'dienstleister_systempartnerprodukttyp_notify_users_angebot',
        records: '~211',
        description: 'Quote notification settings',
        schema: getTableSchema(
          'dienstleister_systempartnerprodukttyp_notify_users_angebot'
        )
      },
      {
        name: 'dienstleister_systempartnerprodukttyp_notify_users_auftrag',
        records: '~205',
        description: 'Order notification settings',
        schema: getTableSchema(
          'dienstleister_systempartnerprodukttyp_notify_users_auftrag'
        )
      },
      {
        name: 'dienstleister_fachpartnerprodukttyp_notify_users_angebot',
        records: '~1,051',
        description: 'Specialist quote notifications',
        schema: getTableSchema(
          'dienstleister_fachpartnerprodukttyp_notify_users_angebot'
        )
      },
      {
        name: 'dienstleister_umsetzungspartnerprodukttyp_notify_users_freigabe',
        records: '~37',
        description: 'Implementation approval notifications',
        schema: getTableSchema(
          'dienstleister_umsetzungspartnerprodukttyp_notify_users_freigabe'
        )
      },
      {
        name: 'dienstleister_vdlprodukttyp_notify_users_freigabe',
        records: '~76',
        description: 'Sales partner approval notifications',
        schema: getTableSchema(
          'dienstleister_vdlprodukttyp_notify_users_freigabe'
        )
      }
    ],
    relationships: [
      'All dienstleister_* → mandant_mandant (tenant assignments)',
      'systempartner ↔ fachpartner (supplier-specialist relationships)',
      'fachpartner → kunde_postleitzahl (regional coverage)',
      'All partners → produkttyp (capability assignments)',
      'Partners → account_emailuser (staff assignments)',
      'Partners → projekt_projekt (project assignments)'
    ]
  },

  // Product Catalog (15 tables) - continuing with similar pattern for all categories...
  products: {
    icon: Package,
    color: 'bg-orange-500',
    description:
      'Product hierarchy, packages, variants, and tenant customizations',
    tables: [
      {
        name: 'produkt_produkttyp',
        records: '~62',
        description: 'Top-level product categories (solar, storage, etc.)',
        schema: getTableSchema('produkt_produkttyp')
      },
      {
        name: 'produkt_produkt',
        records: '~83',
        description: 'Base product catalog with building kit features',
        schema: getTableSchema('produkt_produkt')
      },
      {
        name: 'produkt_mandantprodukt',
        records: '~1,102',
        description: 'Tenant-specific product configurations and pricing',
        schema: getTableSchema('produkt_mandantprodukt')
      },
      {
        name: 'produkt_mandantprodukttyp',
        records: '~795',
        description: 'Tenant-specific product type settings',
        schema: getTableSchema('produkt_mandantprodukttyp')
      },
      {
        name: 'produkt_produktvariante',
        records: '~64',
        description: 'Product variants for different configurations',
        schema: getTableSchema('produkt_produktvariante')
      },
      {
        name: 'produkt_produktvarianteadresse',
        records: '~251,470',
        description: 'Address-specific product availability',
        schema: getTableSchema('produkt_produktvarianteadresse')
      },
      {
        name: 'produkt_produktvarianteadresse_produkt_varianten',
        records: '~263,901',
        description: 'Variant to address mapping table',
        schema: getTableSchema(
          'produkt_produktvarianteadresse_produkt_varianten'
        )
      },
      {
        name: 'paket_paket',
        records: '~194,305',
        description: 'Product packages with specifications and UUID tracking',
        schema: getTableSchema('paket_paket')
      },
      {
        name: 'paket_paketfreifeld',
        records: '~229,772',
        description: 'Custom fields and attributes for packages',
        schema: getTableSchema('paket_paketfreifeld')
      },
      {
        name: 'paket_paketfrontendinformation',
        records: '~13,090',
        description: 'Package display information for customer portals',
        schema: getTableSchema('paket_paketfrontendinformation')
      },
      {
        name: 'paket_paketfrontendkurzbeschreibung',
        records: '~43,122',
        description: 'Package short descriptions and marketing text',
        schema: getTableSchema('paket_paketfrontendkurzbeschreibung')
      },
      {
        name: 'paket_mandantpaket',
        records: '~227,339',
        description: 'Tenant-specific package assignments',
        schema: getTableSchema('paket_mandantpaket')
      },
      {
        name: 'produkt_mandantprodukttyp_email_forward_to',
        records: '~2,046',
        description: 'Email routing for product inquiries',
        schema: getTableSchema('produkt_mandantprodukttyp_email_forward_to')
      },
      {
        name: 'produkt_mandantprodukttyp_users_to_notify_on_projekt_created',
        records: '~1,701',
        description: 'Project creation notification lists',
        schema: getTableSchema(
          'produkt_mandantprodukttyp_users_to_notify_on_projekt_created'
        )
      }
    ],
    relationships: [
      'produkttyp → produkt (hierarchy)',
      'produkt → paket_paket (package assignments)',
      'All product tables → mandant_mandant (tenant customization)',
      'paket_paket → dienstleister_systempartner (supplier assignments)',
      'produktvarianteadresse → kunde_postleitzahl (geographic availability)'
    ]
  },

  // Energy Solutions (15 tables)
  energy: {
    icon: Settings,
    color: 'bg-yellow-500',
    description:
      'Solar roof and energy storage specific configurations and calculations',
    tables: [
      // Solar Energy Systems
      {
        name: 'energiedach_edleistungspaket',
        records: '~132,299',
        description: 'Solar roof packages with power ranges and hardware specs',
        schema: getTableSchema('energiedach_edleistungspaket')
      },
      {
        name: 'energiedach_edparameter',
        records: '~712,408',
        description: 'Solar calculation parameters (financial, technical)',
        schema: getTableSchema('energiedach_edparameter')
      },
      {
        name: 'energiedach_edprojekt',
        records: '~345,210',
        description: 'Solar project configurations and consumption profiles',
        schema: getTableSchema('energiedach_edprojekt')
      },
      {
        name: 'energiedach_eegverguetung',
        records: '~33',
        description: 'German EEG feed-in tariff rates by period',
        schema: getTableSchema('energiedach_eegverguetung')
      },
      {
        name: 'energiedach_globaleparameter',
        records: '~2',
        description: 'Global solar system parameters',
        schema: getTableSchema('energiedach_globaleparameter')
      },
      {
        name: 'energiedach_postleitzahlenbereich',
        records: '~35',
        description: 'Solar irradiation data by postal code region',
        schema: getTableSchema('energiedach_postleitzahlenbereich')
      },
      {
        name: 'energiedach_tetraeder',
        records: '~820,895',
        description: 'Detailed solar irradiation database by address',
        schema: getTableSchema('energiedach_tetraeder')
      },
      // Energy Storage Systems
      {
        name: 'energiespeicher_espeicherpaket',
        records: '~73,883',
        description: 'Battery storage packages with capacity specifications',
        schema: getTableSchema('energiespeicher_espeicherpaket')
      },
      {
        name: 'energiespeicher_espeicherparameter',
        records: '~6,720',
        description: 'Energy storage calculation parameters',
        schema: getTableSchema('energiespeicher_espeicherparameter')
      },
      {
        name: 'energiespeicher_espeicherprojekt',
        records: '~6,495',
        description: 'Storage project configurations',
        schema: getTableSchema('energiespeicher_espeicherprojekt')
      },
      {
        name: 'energiespeicher_globaleparameter',
        records: '~2',
        description: 'Global storage system parameters',
        schema: getTableSchema('energiespeicher_globaleparameter')
      },
      // Load Profiles & Residual Power
      {
        name: 'berechnung_lastprofil',
        records: '~8,761',
        description: 'Energy consumption load profiles (H0-H3, G0-G5)',
        schema: getTableSchema('berechnung_lastprofil')
      },
      {
        name: 'reststrom_reststrom',
        records: '~2,462',
        description: 'Residual power supply configurations',
        schema: getTableSchema('reststrom_reststrom')
      },
      {
        name: 'reststrom_reststromkonfiguration',
        records: '~12',
        description: 'Residual power tariff configurations',
        schema: getTableSchema('reststrom_reststromkonfiguration')
      },
      {
        name: 'reststrom_reststromtarif',
        records: '~108,856',
        description: 'Residual power tariffs by postal code',
        schema: getTableSchema('reststrom_reststromtarif')
      }
    ],
    relationships: [
      'energiedach_edleistungspaket → paket_paket (package inheritance)',
      'energiespeicher_espeicherpaket → paket_paket (package inheritance)',
      'edparameter, espeicherparameter → mandant_mandant (tenant-specific)',
      'edprojekt, espeicherprojekt → projekt_projekt (project configurations)',
      'tetraeder, postleitzahlenbereich → kunde_postleitzahl (geographic data)',
      'reststrom_* → projekt_projekt (residual power supply)'
    ]
  },

  // Dynamic Pricing (13 tables)
  dynamic: {
    icon: Settings,
    color: 'bg-gray-600',
    description:
      'Complex dynamic pricing engine with cost modeling and parameters',
    tables: [
      {
        name: 'dynamic_dynamicpaket',
        records: '~16,375',
        description: 'Dynamic pricing enabled packages',
        schema: getTableSchema('dynamic_dynamicpaket')
      },
      {
        name: 'dynamic_dynamicparameter',
        records: '~120,009',
        description: 'Core dynamic pricing parameters per tenant/product',
        schema: getTableSchema('dynamic_dynamicparameter')
      },
      {
        name: 'dynamic_dynamicparametertemplate',
        records: '~2',
        description: 'Parameter templates for new configurations',
        schema: getTableSchema('dynamic_dynamicparametertemplate')
      },
      {
        name: 'dynamic_dynamicprojekt',
        records: '~119,497',
        description: 'Dynamic pricing project assignments',
        schema: getTableSchema('dynamic_dynamicprojekt')
      },
      {
        name: 'dynamic_dynamickostenelement',
        records: '~387,952',
        description: 'Individual cost elements with complex calculations',
        schema: getTableSchema('dynamic_dynamickostenelement')
      },
      {
        name: 'dynamic_dynamickostenelementtemplate',
        records: '~18',
        description: 'Cost element templates',
        schema: getTableSchema('dynamic_dynamickostenelementtemplate')
      },
      {
        name: 'dynamic_dynamicblockpriceposition',
        records: '~20,221',
        description: 'Block-based pricing positions',
        schema: getTableSchema('dynamic_dynamicblockpriceposition')
      },
      {
        name: 'dynamic_dynamicblockpricepositiontemplate',
        records: '~362,908',
        description: 'Block pricing templates',
        schema: getTableSchema('dynamic_dynamicblockpricepositiontemplate')
      },
      {
        name: 'dynamic_dynamicextracostposition',
        records: '~207',
        description: 'Extra cost positions and surcharges',
        schema: getTableSchema('dynamic_dynamicextracostposition')
      },
      {
        name: 'dynamic_dynamicextracostpositiontemplate',
        records: '~12,738',
        description: 'Extra cost templates',
        schema: getTableSchema('dynamic_dynamicextracostpositiontemplate')
      },
      {
        name: 'dynamic_energy_data',
        records: '~36',
        description: 'Dynamic energy data calculations',
        schema: getTableSchema('dynamic_energy_data')
      },
      {
        name: 'dynamic_energy_data_energy',
        records: '~36',
        description: 'Energy calculation details',
        schema: getTableSchema('dynamic_energy_data_energy')
      },
      {
        name: 'dynamic_energy_data_energy_media',
        records: '~16',
        description: 'Energy calculation supporting documents',
        schema: getTableSchema('dynamic_energy_data_energy_media')
      }
    ],
    relationships: [
      'dynamic_dynamicparameter → mandant_mandant, produkttyp (scope)',
      'dynamic_dynamicprojekt → projekt_projekt (project pricing)',
      'dynamic_dynamicpaket → paket_paket (package pricing)',
      'All cost elements → dynamic_dynamicparameter (parameter hierarchy)',
      'Templates → live configurations (inheritance pattern)'
    ]
  },

  // Qualification System (12 tables)
  qualification: {
    icon: Database,
    color: 'bg-pink-500',
    description:
      'Complex project qualification system with parameters, conditions, and values',
    tables: [
      {
        name: 'qualifikation_projektqualifikationswert',
        records: '~18,689,273',
        description:
          'Project qualification values (18.7M records - largest table)',
        schema: getTableSchema('qualifikation_projektqualifikationswert')
      },
      {
        name: 'qualifikation_projektqualifikationsparameter',
        records: '~18,795',
        description: 'Qualification parameters per tenant/product type',
        schema: getTableSchema('qualifikation_projektqualifikationsparameter')
      },
      {
        name: 'qualifikation_basisprojektqualifikationsparameter',
        records: '~1,718',
        description: 'Base qualification parameter definitions',
        schema: getTableSchema(
          'qualifikation_basisprojektqualifikationsparameter'
        )
      },
      {
        name: 'qualifikation_projektqualifikationsparameterchoice',
        records: '~21,922',
        description: 'Choice options for qualification parameters',
        schema: getTableSchema(
          'qualifikation_projektqualifikationsparameterchoice'
        )
      },
      {
        name: 'qualifikation_projektqualifikationswert_value_multiplechoices',
        records: '~106,603',
        description: 'Multiple choice value assignments',
        schema: getTableSchema(
          'qualifikation_projektqualifikationswert_value_multiplechoices'
        )
      },
      {
        name: 'qualifikation_qualificationcategory',
        records: '~4',
        description: 'Qualification parameter categories',
        schema: getTableSchema('qualifikation_qualificationcategory')
      },
      {
        name: 'qualifikation_bedingung',
        records: '~8,196',
        description: 'Legacy qualification conditions',
        schema: getTableSchema('qualifikation_bedingung')
      },
      {
        name: 'qualifikation_pqpbasebedingung',
        records: '~8,054',
        description: 'Base conditions for parameters',
        schema: getTableSchema('qualifikation_pqpbasebedingung')
      },
      {
        name: 'qualifikation_paketbedingung',
        records: '~4,270',
        description: 'Package-based qualification rules',
        schema: getTableSchema('qualifikation_paketbedingung')
      },
      {
        name: 'qualifikation_optionbedingung',
        records: '~361',
        description: 'Option-based qualification rules',
        schema: getTableSchema('qualifikation_optionbedingung')
      },
      {
        name: 'qualifikation_buildingkitcondition',
        records: '~2,631',
        description: 'Building kit qualification conditions',
        schema: getTableSchema('qualifikation_buildingkitcondition')
      },
      {
        name: 'qualifikation_buildingblockcondition',
        records: '~0',
        description: 'Building block qualification conditions',
        schema: getTableSchema('qualifikation_buildingblockcondition')
      }
    ],
    relationships: [
      'qualifikationswert → projekt_projekt (qualification data per project)',
      'parameter → mandantprodukttyp (tenant/product scoping)',
      'basisprojektqualifikationsparameter → qualificationcategory (categorization)',
      'Choice tables → parameter (option hierarchies)',
      'Condition tables → parameter (business rules)',
      'Building kit conditions → vlink_buildingkit_* (modular conditions)'
    ]
  },

  // Business Processes (9 tables)
  business: {
    icon: FileText,
    color: 'bg-red-600',
    description:
      'Sales pipeline, order management, and project execution workflows',
    tables: [
      {
        name: 'angebot_angebot',
        records: '~478,680',
        description: 'Quotes and offers with status tracking',
        schema: getTableSchema('angebot_angebot')
      },
      {
        name: 'angebot_indication',
        records: '~2,738',
        description: 'Indicative offers for preliminary pricing',
        schema: getTableSchema('angebot_indication')
      },
      {
        name: 'bestellung_bestellung',
        records: '~31,091',
        description: 'Purchase orders and procurement tracking',
        schema: getTableSchema('bestellung_bestellung')
      },
      {
        name: 'abwicklung_abwicklung',
        records: '~471,835',
        description: 'Project execution with milestone tracking',
        schema: getTableSchema('abwicklung_abwicklung')
      },
      {
        name: 'abwicklung_abwicklungmedia',
        records: '~572,309',
        description: 'Execution documentation and media files',
        schema: getTableSchema('abwicklung_abwicklungmedia')
      },
      {
        name: 'abwicklung_hardwarelieferant',
        records: '~14',
        description: 'Hardware supplier assignments',
        schema: getTableSchema('abwicklung_hardwarelieferant')
      },
      {
        name: 'logistics_logistics',
        records: '~8,252',
        description: 'Logistics and shipping coordination',
        schema: getTableSchema('logistics_logistics')
      },
      {
        name: 'logistics_logisticsshippingitem',
        records: '~11,706',
        description: 'Individual shipping items with tracking',
        schema: getTableSchema('logistics_logisticsshippingitem')
      },
      {
        name: 'logistics_logisticsretoureitem',
        records: '~184',
        description: 'Return and exchange item tracking',
        schema: getTableSchema('logistics_logisticsretoureitem')
      }
    ],
    relationships: [
      'angebot_angebot → projekt_projekt (quotes per project)',
      'angebot_angebot → dienstleister_systempartner (supplier quotes)',
      'bestellung_bestellung → projekt_projekt (orders per project)',
      'abwicklung_abwicklung → projekt_projekt (1:1 execution tracking)',
      'logistics_logistics → projekt_projekt (shipping coordination)',
      'All process tables → account_emailuser (user assignments)'
    ]
  },

  // Communication (9 tables)
  communication: {
    icon: MessageSquare,
    color: 'bg-blue-600',
    description:
      'Communication feeds, document management, and notification systems',
    tables: [
      {
        name: 'feed_post',
        records: '~3,219,809',
        description:
          'Communication feed with messages and updates (3.2M records)',
        schema: getTableSchema('feed_post')
      },
      {
        name: 'feed_postfile',
        records: '~895,873',
        description: 'File attachments for communication posts',
        schema: getTableSchema('feed_postfile')
      },
      {
        name: 'dokument_dokumentvorlage',
        records: '~14,945',
        description: 'Document templates for various business processes',
        schema: getTableSchema('dokument_dokumentvorlage')
      },
      {
        name: 'dokument_dokumentkategorie',
        records: '~99',
        description: 'Hierarchical document categorization',
        schema: getTableSchema('dokument_dokumentkategorie')
      },
      {
        name: 'dokument_dokumentvorlage_attachment_templates',
        records: '~1,385',
        description: 'Template attachment relationships',
        schema: getTableSchema('dokument_dokumentvorlage_attachment_templates')
      },
      {
        name: 'dokument_dokumentvorlage_produkttypen',
        records: '~29,065',
        description: 'Template to product type assignments',
        schema: getTableSchema('dokument_dokumentvorlage_produkttypen')
      },
      {
        name: 'kommunikation_kommunikationsaktion',
        records: '~445',
        description: 'Automated communication workflows',
        schema: getTableSchema('kommunikation_kommunikationsaktion')
      },
      {
        name: 'kommunikation_meetingreminder',
        records: '~0',
        description: 'Meeting reminder configurations',
        schema: getTableSchema('kommunikation_meetingreminder')
      },
      {
        name: 'notification_notification',
        records: '~67,449,429',
        description:
          'System notifications (67.4M records - second largest table)',
        schema: getTableSchema('notification_notification')
      }
    ],
    relationships: [
      'feed_post → account_emailuser, kunde_kunde (communication participants)',
      'feed_post → content_type, object_id (generic relationships)',
      'dokument_* → mandant_mandant, dienstleister_* (template ownership)',
      'kommunikation_* → produkttyp, mandant (workflow scoping)',
      'notification → account_emailuser (notification delivery)'
    ]
  },

  // Frontend & Portals (6 tables)
  frontend: {
    icon: Globe,
    color: 'bg-cyan-500',
    description:
      'Customer-facing interfaces, portals, and frontend configurations',
    tables: [
      {
        name: 'frontend_frontendv2',
        records: '~688',
        description: 'Frontend configurations with JSON layouts and content',
        schema: getTableSchema('frontend_frontendv2')
      },
      {
        name: 'frontend_frontendv2_verfuegbarkeit_plz',
        records: '~426,925',
        description: 'Frontend postal code availability mapping',
        schema: getTableSchema('frontend_frontendv2_verfuegbarkeit_plz')
      },
      {
        name: 'kundenportal_kundenportal',
        records: '~22',
        description: 'Customer portal instances and configurations',
        schema: getTableSchema('kundenportal_kundenportal')
      },
      {
        name: 'kundenportal_kundenportalstatuskonfiguration',
        records: '~382',
        description: 'Portal status display configurations',
        schema: getTableSchema('kundenportal_kundenportalstatuskonfiguration')
      },
      {
        name: 'baukasten_welt_buildingkitfrontendinformation',
        records: '~27',
        description: 'Building kit frontend presentation',
        schema: getTableSchema('baukasten_welt_buildingkitfrontendinformation')
      },
      {
        name: 'baukasten_welt_buildingkitfrontendshortdescription',
        records: '~104',
        description: 'Building kit short descriptions',
        schema: getTableSchema(
          'baukasten_welt_buildingkitfrontendshortdescription'
        )
      }
    ],
    relationships: [
      'frontend_frontendv2 → produkt_mandantprodukttyp (tenant/product scoping)',
      'frontendv2_verfuegbarkeit_plz → kunde_postleitzahl (geographic availability)',
      'kundenportal_* → mandantprodukttyp (portal scoping)',
      'baukasten_* → vlink_buildingkit_* (building kit integration)'
    ]
  },

  // Building Kit System (23 tables)
  buildingkit: {
    icon: Package,
    color: 'bg-purple-600',
    description:
      'Advanced modular building kit system for complex product configurations',
    tables: [
      // Core Building Kit
      {
        name: 'vlink_buildingkit_accessor',
        records: '~23,902',
        description: 'Access control for building kit entities',
        schema: getTableSchema('vlink_buildingkit_accessor')
      },
      {
        name: 'vlink_buildingkit_buildingkit',
        records: '~39,213',
        description: 'Building kit definitions with ownership',
        schema: getTableSchema('vlink_buildingkit_buildingkit')
      },
      {
        name: 'vlink_buildingkit_buildingkitinfo',
        records: '~4,106',
        description: 'Building kit metadata and categories',
        schema: getTableSchema('vlink_buildingkit_buildingkitinfo')
      },
      {
        name: 'vlink_buildingkit_buildingblock',
        records: '~49,799',
        description: 'Individual building blocks with trading margins',
        schema: getTableSchema('vlink_buildingkit_buildingblock')
      },
      {
        name: 'vlink_buildingkit_buildingblocktemplate',
        records: '~33,449',
        description: 'Building block templates and definitions',
        schema: getTableSchema('vlink_buildingkit_buildingblocktemplate')
      },
      // Relationships & Access
      {
        name: 'vlink_buildingkit_buildingkitinfo_consumers',
        records: '~9,527',
        description: 'Building kit access permissions',
        schema: getTableSchema('vlink_buildingkit_buildingkitinfo_consumers')
      },
      {
        name: 'vlink_buildingkit_buildingblock_consumers',
        records: '~105,987',
        description: 'Building block access permissions',
        schema: getTableSchema('vlink_buildingkit_buildingblock_consumers')
      },
      {
        name: 'vlink_buildingkit_blockkitrelation',
        records: '~190,018',
        description: 'Block to kit relationships with quantities',
        schema: getTableSchema('vlink_buildingkit_blockkitrelation')
      },
      {
        name: 'vlink_buildingkit_tradingmarginrelation',
        records: '~458',
        description: 'Trading margin relationships between entities',
        schema: getTableSchema('vlink_buildingkit_tradingmarginrelation')
      },
      // Dynamic Fields System
      {
        name: 'vlink_buildingkit_field',
        records: '~80',
        description: 'Dynamic field definitions for blocks',
        schema: getTableSchema('vlink_buildingkit_field')
      },
      {
        name: 'vlink_buildingkit_fieldchoice',
        records: '~18',
        description: 'Choice options for dynamic fields',
        schema: getTableSchema('vlink_buildingkit_fieldchoice')
      },
      {
        name: 'vlink_buildingkit_templatefieldrelation',
        records: '~126,583',
        description: 'Template to field relationships',
        schema: getTableSchema('vlink_buildingkit_templatefieldrelation')
      },
      // Field Values (by type)
      {
        name: 'vlink_buildingkit_textfieldvalue',
        records: '~29,716',
        description: 'Text field values for building blocks',
        schema: getTableSchema('vlink_buildingkit_textfieldvalue')
      },
      {
        name: 'vlink_buildingkit_decimalfieldvalue',
        records: '~123,480',
        description: 'Decimal field values for building blocks',
        schema: getTableSchema('vlink_buildingkit_decimalfieldvalue')
      },
      {
        name: 'vlink_buildingkit_integerfieldvalue',
        records: '~2,845',
        description: 'Integer field values for building blocks',
        schema: getTableSchema('vlink_buildingkit_integerfieldvalue')
      },
      {
        name: 'vlink_buildingkit_booleanfieldvalue',
        records: '~0',
        description: 'Boolean field values for building blocks',
        schema: getTableSchema('vlink_buildingkit_booleanfieldvalue')
      },
      {
        name: 'vlink_buildingkit_choicefieldvalue',
        records: '~5,892',
        description: 'Choice field values for building blocks',
        schema: getTableSchema('vlink_buildingkit_choicefieldvalue')
      },
      // Additional Features
      {
        name: 'vlink_buildingkit_extracost',
        records: '~2,006',
        description: 'Extra costs and surcharges for building kits',
        schema: getTableSchema('vlink_buildingkit_extracost')
      },
      {
        name: 'vlink_buildingkit_buildingunitattachment',
        records: '~152,239',
        description: 'File attachments for building units',
        schema: getTableSchema('vlink_buildingkit_buildingunitattachment')
      },
      // Version Control
      {
        name: 'vlink_buildingkit_frozenkitversion',
        records: '~3,894',
        description: 'Frozen versions for configuration stability',
        schema: getTableSchema('vlink_buildingkit_frozenkitversion')
      },
      {
        name: 'vlink_buildingkit_frozenkitversion_individualized_relations',
        records: '~23,595',
        description: 'Individualized relations in frozen versions',
        schema: getTableSchema(
          'vlink_buildingkit_frozenkitversion_individualized_relations'
        )
      },
      {
        name: 'vlink_buildingkit_field_frozen_versions',
        records: '~58,734',
        description: 'Field frozen version relationships',
        schema: getTableSchema('vlink_buildingkit_field_frozen_versions')
      },
      {
        name: 'vlink_buildingkit_fieldchoice_frozen_versions',
        records: '~10,273',
        description: 'Field choice frozen version relationships',
        schema: getTableSchema('vlink_buildingkit_fieldchoice_frozen_versions')
      }
    ],
    relationships: [
      'buildingkit → produkt_produkt (product category assignments)',
      'buildingkit_accessor → account_emailuser, dienstleister_* (access control)',
      'buildingblock → buildingblocktemplate (template inheritance)',
      'blockkitrelation → buildingblock + buildingkit (composition)',
      'field values → buildingblock + field (dynamic attributes)',
      'frozen versions → buildingkit (version control)'
    ]
  },

  // System Infrastructure (17 tables)
  system: {
    icon: Database,
    color: 'bg-gray-800',
    description:
      'System infrastructure, tracking, permissions, and utility tables',
    tables: [
      // Activity & Tracking
      {
        name: 'actstream_action',
        records: '~24,145,236',
        description:
          'Activity stream for comprehensive audit trail (24M records)',
        schema: getTableSchema('actstream_action')
      },
      {
        name: 'tracking_statuswechsel',
        records: '~6,769,393',
        description:
          'Status change tracking across all entities (6.8M records)',
        schema: getTableSchema('tracking_statuswechsel')
      },
      {
        name: 'tracking_intermediatestatuswechsel',
        records: '~241',
        description: 'Intermediate status change tracking',
        schema: getTableSchema('tracking_intermediatestatuswechsel')
      },
      {
        name: 'reversion_revision',
        records: '~3,342,065',
        description: 'Version control revision history (3.3M records)',
        schema: getTableSchema('reversion_revision')
      },
      {
        name: 'reversion_version',
        records: '~3,342,065',
        description: 'Version control object versions (3.3M records)',
        schema: getTableSchema('reversion_version')
      },
      // Permissions & Groups
      {
        name: 'permission_permissiongroup_permissions',
        records: '~29,083',
        description: 'Permission group to permission assignments',
        schema: getTableSchema('permission_permissiongroup_permissions')
      },
      {
        name: 'permission_permissiongroup_produkttypen',
        records: '~3',
        description: 'Product type permission scoping',
        schema: getTableSchema('permission_permissiongroup_produkttypen')
      },
      // Tags & Options
      {
        name: 'tags_tag',
        records: '~73,447',
        description: 'Public tagging system with creator tracking',
        schema: getTableSchema('tags_tag')
      },
      {
        name: 'tags_intern_tag',
        records: '~3,512',
        description: 'Internal/private tagging system',
        schema: getTableSchema('tags_intern_tag')
      },
      {
        name: 'option_option',
        records: '~10,306,559',
        description: 'Project options and add-ons (10.3M records)',
        schema: getTableSchema('option_option')
      },
      // Sales Channels
      {
        name: 'vertrieb_vertriebskanal',
        records: '~206',
        description: 'Sales channel definitions',
        schema: getTableSchema('vertrieb_vertriebskanal')
      },
      {
        name: 'vertrieb_vertriebskanalmandant',
        records: '~503',
        description: 'Sales channel to tenant assignments',
        schema: getTableSchema('vertrieb_vertriebskanalmandant')
      },
      {
        name: 'vertrieb_vertriebskanalmandantdynamicparameter',
        records: '~67',
        description: 'Dynamic pricing per sales channel',
        schema: getTableSchema('vertrieb_vertriebskanalmandantdynamicparameter')
      },
      // Utilities
      {
        name: 'core_rundung',
        records: '~9,056,467',
        description: 'Rounding rules for financial calculations (9M records)',
        schema: getTableSchema('core_rundung')
      },
      {
        name: 'core_attachment',
        records: '~1,988,437',
        description: 'Generic file attachment system (2M records)',
        schema: getTableSchema('core_attachment')
      }
    ],
    relationships: [
      'actstream_action → ALL entities (activity tracking)',
      'tracking_statuswechsel → projekt_projekt (status monitoring)',
      'reversion_* → ALL entities (version control)',
      'tags_* → projekt_projekt (tagging)',
      'option_option → projekt_projekt, paket_paket (add-ons)',
      'vertrieb_* → mandant_mandant (sales channel routing)',
      'core_attachment → ALL entities via content_type (generic attachments)'
    ]
  }
}
